import { useAssistantRuntime } from '@assistant-ui/react'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAgentSidebar } from '@/components/layout/AgentSidebarContext'
import { Button } from '@/components/ui/button'
import { prepareResolutionConversation } from './assistant-prompt'
import { agentThreadHealthKey } from './useAgentRuntime'

type Prompt = { caseId: string; text: string }
type Thread = { id: string; ready: boolean }
type Preparation = {
  request: Prompt
  attempt: number
  promise: ReturnType<typeof prepareResolutionConversation>
}

export function appendAssistantPrompt(draft: string, prompt: string): string {
  return draft.trim() ? `${draft.trimEnd()}\n\n${prompt}` : prompt
}

/** Reattach to the same preparation after StrictMode's effect replay; a mutation's
 * per-call callback can be lost when its observer unsubscribes during that replay. */
function usePreparedPrompt(caseId: string | undefined, thread: Thread) {
  const runtime = useAssistantRuntime()
  const client = useQueryClient()
  const { requestedPrompt, consumePromptRequest } = useAgentSidebar()
  const pending = useRef<Preparation | null>(null)
  const applied = useRef<Prompt | null>(null)
  const [attempt, setAttempt] = useState(0)
  const [failed, setFailed] = useState(false)
  useEffect(() => {
    if (
      !thread.ready ||
      !requestedPrompt ||
      requestedPrompt.caseId !== caseId ||
      applied.current === requestedPrompt
    )
      return
    let cancelled = false
    setFailed(false)
    if (pending.current?.request !== requestedPrompt || pending.current.attempt !== attempt) {
      pending.current = {
        request: requestedPrompt,
        attempt,
        promise: prepareResolutionConversation(caseId, thread.id),
      }
    }
    pending.current.promise
      .then(health => {
        if (cancelled) return
        client.setQueryData(agentThreadHealthKey(caseId, thread.id), health)
        const composer = runtime.thread.composer
        composer.setText(appendAssistantPrompt(composer.getState().text, requestedPrompt.text))
        applied.current = requestedPrompt
        consumePromptRequest()
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })
    return () => {
      cancelled = true
    }
  }, [
    attempt,
    caseId,
    thread.id,
    thread.ready,
    requestedPrompt,
    runtime,
    client,
    consumePromptRequest,
  ])
  return {
    failed: failed && requestedPrompt?.caseId === caseId,
    retry: () => setAttempt(value => value + 1),
  }
}

/** The launch button prepares a normal-chat draft; sending remains the user's action. */
export function SidebarPromptDraft({ caseId, thread }: { caseId?: string; thread: Thread }) {
  const { t } = useTranslation()
  const { failed, retry } = usePreparedPrompt(caseId, thread)
  if (!failed) return null
  return (
    <div className="space-y-2 p-3 text-sm" role="alert">
      <p className="text-destructive">{t('chat.toolsPanel.saveFailed')}</p>
      <Button variant="outline" size="sm" onClick={retry}>
        {t('chat.retryFailedSend')}
      </Button>
    </div>
  )
}
